import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Wrench,
  Plus,
  RefreshCw,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Timer,
  Play,
  CheckCircle,
  XCircle,
  RotateCcw,
  MapPin,
  Pencil,
} from "lucide-react";
import { type ColumnDef } from "@tanstack/react-table";
import { DataTable } from "../components/ui/DataTable";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { StatusBadge } from "../components/ui/StatusBadge";
import { KPICard } from "../components/ui/KPICard";
import { Modal } from "../components/ui/Modal";
import { Input } from "../components/ui/Input";
import { DMSDocumentsPanel } from "../components/dms/DMSDocumentsPanel";
import api from "../lib/api";
import { useAppStore } from "../stores/appStore";
import { formatDate } from "../lib/utils";
import {
  type MaintenanceStatus,
  type MaintenanceTask,
  listMaintenanceTasks,
  updateMaintenanceTask,
} from "../lib/maintenanceApi";

/** Local view of /assets list items. The backend embeds `functional_location`
 *  inline (added on the asset register itself) so the table can render +
 *  filter by FL without a second fetch. */
interface AssetFlocSummary {
  id: string;
  code: string;
  name: string;
  level: number;
}

interface AssetOption {
  id: string;
  name: string;
  asset_code: string;
  functional_location_id?: string | null;
  functional_location?: AssetFlocSummary | null;
}

const STATUS_OPTIONS = [
  "scheduled",
  "in_progress",
  "completed",
  "cancelled",
  "overdue",
] as const;

const PRIORITY_OPTIONS = ["low", "medium", "high", "critical"] as const;

const TASK_TYPES = [
  "inspection",
  "preventive",
  "corrective",
  "predictive",
  "emergency",
  "calibration",
] as const;

type TransitionKind = "start" | "complete" | "cancel" | "reopen";

interface TransitionConfig {
  targetStatus: MaintenanceStatus;
  title: string;
  confirmLabel: string;
  needsCompletedDate: boolean;
  needsNotes: boolean;
  notesLabel: string;
  notesRequired: boolean;
}

const TRANSITION_CONFIG: Record<TransitionKind, TransitionConfig> = {
  start: {
    targetStatus: "in_progress",
    title: "Start Task",
    confirmLabel: "Start",
    needsCompletedDate: false,
    needsNotes: true,
    notesLabel: "Notes (optional)",
    notesRequired: false,
  },
  complete: {
    targetStatus: "completed",
    title: "Complete Task",
    confirmLabel: "Mark Complete",
    needsCompletedDate: true,
    needsNotes: true,
    notesLabel: "Completion Notes",
    notesRequired: true,
  },
  cancel: {
    targetStatus: "cancelled",
    title: "Cancel Task",
    confirmLabel: "Cancel Task",
    needsCompletedDate: false,
    needsNotes: true,
    notesLabel: "Reason",
    notesRequired: true,
  },
  reopen: {
    targetStatus: "scheduled",
    title: "Reopen Task",
    confirmLabel: "Reopen",
    needsCompletedDate: false,
    needsNotes: true,
    notesLabel: "Reason for reopening",
    notesRequired: false,
  },
};

function availableTransitions(status: string): TransitionKind[] {
  switch (status) {
    case "scheduled":
      return ["start", "complete", "cancel"];
    case "in_progress":
      return ["complete", "cancel"];
    case "overdue":
      return ["start", "complete", "cancel"];
    case "completed":
    case "cancelled":
      return ["reopen"];
    default:
      return [];
  }
}

export default function Maintenance() {
  const { selectedOrgId, selectedSiteId } = useAppStore();
  const [searchParams, setSearchParams] = useSearchParams();
  // Once a `?task=<id>` deep link is consumed we remember it so a later
  // task-list refresh doesn't re-open the edit modal.
  const consumedTaskParamRef = useRef<string | null>(null);
  const [tasks, setTasks] = useState<MaintenanceTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [assets, setAssets] = useState<AssetOption[]>([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [createForm, setCreateForm] = useState({
    asset_id: "",
    task_type: "inspection",
    description: "",
    priority: "medium",
    scheduled_date: "",
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Transition modal state — opened from the Actions column for an existing task.
  const [transitioning, setTransitioning] = useState<{
    task: MaintenanceTask;
    kind: TransitionKind;
  } | null>(null);
  const [transitionForm, setTransitionForm] = useState({
    completed_date: "",
    notes: "",
  });
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [isApplyingTransition, setIsApplyingTransition] = useState(false);

  // Edit modal state — modifies non-status fields (task_type, description,
  // scheduled_date, priority, notes). Status transitions live in the
  // separate transition flow above.
  const [editing, setEditing] = useState<MaintenanceTask | null>(null);
  const [editForm, setEditForm] = useState({
    task_type: "inspection",
    description: "",
    scheduled_date: "",
    priority: "medium",
    notes: "",
  });
  const [editError, setEditError] = useState<string | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const fetchTasks = useCallback(async () => {
    if (!selectedOrgId || !selectedSiteId) {
      setTasks([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const res = await listMaintenanceTasks({
        virtual_site_id: selectedSiteId,
        page,
        page_size: 20,
      });
      setTasks(res.items || []);
      setTotalPages(res.total_pages || 1);
      setTotal(res.total || 0);
    } catch {
      setTasks([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedOrgId, selectedSiteId, page]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Deep-link handler: `?task=<id>` opens the edit modal for that task. Used
  // by the Maintenance tab on the Asset Details page so a click jumps the
  // user straight into the task they care about instead of a generic list.
  useEffect(() => {
    const taskParam = searchParams.get("task");
    if (!taskParam || consumedTaskParamRef.current === taskParam) return;
    if (tasks.length === 0) return;
    const task = tasks.find((t) => t.id === taskParam);
    if (!task) return;
    consumedTaskParamRef.current = taskParam;
    openEdit(task);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("task");
        return next;
      },
      { replace: true },
    );
  // openEdit is stable enough for this purpose; intentionally not in deps so
  // we don't re-trigger when its identity changes after a fetch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, tasks, setSearchParams]);

  // Build an asset_id → AssetOption lookup so the Asset column can render
  // human-readable identifiers without an N+1 fetch per task row. Same query
  // the create modal uses; fetched on mount so the column has data from the
  // first render instead of waiting for the modal to open.
  const assetById = useMemo(() => {
    const m = new Map<string, AssetOption>();
    for (const a of assets) m.set(a.id, a);
    return m;
  }, [assets]);

  const fetchAssetsForSite = useCallback(async () => {
    if (!selectedSiteId) {
      setAssets([]);
      return;
    }
    setIsLoadingAssets(true);
    try {
      const res = await api.get("/assets", {
        params: {
          virtual_site_id: selectedSiteId,
          page: 1,
          // Bumped from 100 → 500 so the FL column (which joins through
          // asset.functional_location_id) resolves for tasks whose asset
          // would otherwise have fallen past the page cap. Matches the
          // Instrumentation page's cap.
          page_size: 500,
          sort_by: "name",
          sort_order: "asc",
        },
      });
      setAssets(res.data.items || []);
    } catch {
      setAssets([]);
    } finally {
      setIsLoadingAssets(false);
    }
  }, [selectedSiteId]);

  // Pull assets up-front (not just when the create modal opens) so the new
  // Asset column on the task table has identifiers to render. Refetched
  // whenever the site changes.
  useEffect(() => {
    fetchAssetsForSite();
  }, [fetchAssetsForSite]);

  // FL summary now ships INSIDE each /assets list item (backend embeds
  // `functional_location` on AssetResponse). No separate /functional-locations
  // fetch needed — the previous one was capped at page_size<=200 and silently
  // 422'd at higher page sizes, leaving the FL column blank on busier sites.
  const flocForAssetId = useCallback(
    (assetId: string | null | undefined): AssetFlocSummary | null => {
      if (!assetId) return null;
      return assetById.get(assetId)?.functional_location ?? null;
    },
    [assetById],
  );

  // FL filter dropdown options — derived from the assets we've loaded so
  // the user only sees FLs that actually belong to at least one asset
  // visible in their task list.
  const flocOptions = useMemo(() => {
    const seen = new Map<string, AssetFlocSummary>();
    for (const a of assets) {
      const fl = a.functional_location;
      if (fl && !seen.has(fl.id)) seen.set(fl.id, fl);
    }
    return Array.from(seen.values()).sort(
      (a, b) => a.level - b.level || a.code.localeCompare(b.code),
    );
  }, [assets]);

  // Per-column filter state is owned by the DataTable (TanStack column
  // filters). All filtering happens via meta.filter on each column below.

  function openCreate() {
    setCreateError(null);
    setCreateForm({
      asset_id: "",
      task_type: "inspection",
      description: "",
      priority: "medium",
      scheduled_date: "",
    });
    setShowCreate(true);
    fetchAssetsForSite();
  }

  async function handleCreate() {
    if (!selectedOrgId || !selectedSiteId) {
      setCreateError("Please select a site first");
      return;
    }
    if (!createForm.asset_id) {
      setCreateError("Please select an asset");
      return;
    }
    if (!createForm.description.trim()) {
      setCreateError("Description is required");
      return;
    }
    if (!createForm.scheduled_date) {
      setCreateError("Scheduled date is required");
      return;
    }
    setIsCreating(true);
    setCreateError(null);
    try {
      await api.post(`/maintenance/tasks`, null, {
        params: {
          asset_id: createForm.asset_id,
          task_type: createForm.task_type,
          description: createForm.description,
          scheduled_date: createForm.scheduled_date,
          priority: createForm.priority,
        },
      });
      setShowCreate(false);
      fetchTasks();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string | Array<{ msg: string }> } } };
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((d) => d.msg).join("; ")
        : detail || "Failed to create task";
      setCreateError(message);
    } finally {
      setIsCreating(false);
    }
  }

  function openEdit(task: MaintenanceTask) {
    setEditError(null);
    setEditForm({
      task_type: task.task_type || "inspection",
      description: task.description || "",
      scheduled_date: task.scheduled_date
        ? task.scheduled_date.slice(0, 10)
        : "",
      priority: task.priority || "medium",
      notes: task.notes || "",
    });
    setEditing(task);
    fetchAssetsForSite();
  }

  async function handleSaveEdit() {
    if (!editing) return;
    if (!editForm.description.trim()) {
      setEditError("Description is required");
      return;
    }
    if (!editForm.scheduled_date) {
      setEditError("Scheduled date is required");
      return;
    }
    setIsSavingEdit(true);
    setEditError(null);
    try {
      // Pass only changed-or-required fields. Empty notes stays empty (the
      // backend treats "" as "no change" because update_task drops falsy
      // values, so we send a single space sentinel to actually clear).
      const payload: Parameters<typeof updateMaintenanceTask>[1] = {
        task_type: editForm.task_type as Parameters<typeof updateMaintenanceTask>[1]["task_type"],
        description: editForm.description.trim(),
        scheduled_date: editForm.scheduled_date,
        priority: editForm.priority as Parameters<typeof updateMaintenanceTask>[1]["priority"],
      };
      // Only send notes when the user actually edited them — same reasoning
      // as the transition flow above. Don't clobber an existing audit trail
      // by writing the unchanged value back.
      if (editForm.notes !== (editing.notes || "")) {
        payload.notes = editForm.notes;
      }
      await updateMaintenanceTask(editing.id, payload);
      setEditing(null);
      fetchTasks();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string | Array<{ msg: string }> } } };
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((d) => d.msg).join("; ")
        : detail || "Failed to update task";
      setEditError(message);
    } finally {
      setIsSavingEdit(false);
    }
  }

  function openTransition(task: MaintenanceTask, kind: TransitionKind) {
    setTransitionError(null);
    setTransitionForm({
      completed_date:
        kind === "complete"
          ? new Date().toISOString().slice(0, 10)
          : "",
      notes: "",
    });
    setTransitioning({ task, kind });
  }

  async function handleTransition() {
    if (!transitioning) return;
    const { task, kind } = transitioning;
    const cfg = TRANSITION_CONFIG[kind];

    if (cfg.needsCompletedDate && !transitionForm.completed_date) {
      setTransitionError("Completion date is required");
      return;
    }
    if (cfg.notesRequired && !transitionForm.notes.trim()) {
      setTransitionError(`${cfg.notesLabel} is required`);
      return;
    }

    setIsApplyingTransition(true);
    setTransitionError(null);
    try {
      const payload: Parameters<typeof updateMaintenanceTask>[1] = {
        status: cfg.targetStatus,
      };
      if (cfg.needsCompletedDate && transitionForm.completed_date) {
        payload.completed_date = transitionForm.completed_date;
      }
      // The backend's update_task only writes truthy values, so we only send
      // notes when the user actually typed something. Mixing existing notes
      // with the new note isn't supported by the API contract — last write
      // wins — and surfacing that behavior here would be misleading.
      if (transitionForm.notes.trim()) {
        const stamp = new Date().toISOString().slice(0, 16).replace("T", " ");
        const newEntry = `[${stamp} → ${cfg.targetStatus}] ${transitionForm.notes.trim()}`;
        payload.notes = task.notes
          ? `${task.notes}\n${newEntry}`
          : newEntry;
      }
      await updateMaintenanceTask(task.id, payload);
      setTransitioning(null);
      fetchTasks();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string | Array<{ msg: string }> } } };
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((d) => d.msg).join("; ")
        : detail || "Failed to update task";
      setTransitionError(message);
    } finally {
      setIsApplyingTransition(false);
    }
  }

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const isOverdueTask = useCallback(
    (t: MaintenanceTask): boolean => {
      if (t.status === "completed" || t.status === "cancelled") return false;
      if (t.status === "overdue") return true;
      if (t.status === "scheduled" && t.scheduled_date) {
        return new Date(t.scheduled_date) < today;
      }
      return false;
    },
    [today],
  );

  const overdue = tasks.filter(isOverdueTask).length;
  const scheduled = tasks.filter(
    (t) => t.status === "scheduled" && !isOverdueTask(t),
  ).length;
  const inProgress = tasks.filter((t) => t.status === "in_progress").length;
  const completed = tasks.filter((t) => t.status === "completed").length;

  const columns: ColumnDef<MaintenanceTask, unknown>[] = [
    {
      accessorKey: "description",
      header: "Task",
      // 18rem → 11rem ≈ 40% reduction. Combined with `truncate` on the
      // text it firmly clamps the rendered cell width without breaking
      // the table layout for narrow descriptions.
      cell: ({ row }) => (
        <div className="flex items-center gap-2 min-w-0 max-w-[11rem]">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-amber-500/15">
            <Wrench className="h-3 w-3 text-amber-400" />
          </div>
          <div className="min-w-0">
            <p className="truncate text-xs font-medium text-surface-100" title={row.original.description}>
              {row.original.description}
            </p>
            {row.original.notes && (
              <p className="truncate text-[10px] text-surface-500">
                {row.original.notes.split("\n").pop()}
              </p>
            )}
          </div>
        </div>
      ),
      // Free-text filter on description + notes. Custom filterFn so the
      // user can type a substring of either field.
      filterFn: (row, _id, value) => {
        const v = typeof value === "string" ? value.trim().toLowerCase() : "";
        if (!v) return true;
        const t = row.original;
        const hay = `${t.description || ""} ${t.notes || ""}`.toLowerCase();
        return hay.includes(v);
      },
      meta: {
        filter: { type: "text", placeholder: "Search task / notes…" },
      },
    },
    {
      id: "asset",
      accessorFn: (row) => row.asset_id,
      header: "Asset",
      cell: ({ row }) => {
        const a = assetById.get(row.original.asset_id);
        if (!a) {
          // Asset lookup hasn't landed yet (or the asset was deleted) — fall
          // back to the raw id so the cell isn't blank.
          return (
            <span className="font-mono text-[10px] text-surface-500">
              {row.original.asset_id.slice(0, 8)}…
            </span>
          );
        }
        return (
          <div className="min-w-0 max-w-[10rem]">
            <p className="truncate text-xs font-medium text-surface-200">
              {a.name}
            </p>
            <p className="truncate font-mono text-[10px] text-surface-500">
              {a.asset_code}
            </p>
          </div>
        );
      },
      filterFn: (row, _id, value) =>
        !value || row.original.asset_id === value,
      meta: {
        filter: {
          type: "select",
          placeholder: "Any asset",
          options: assets.map((a) => ({
            value: a.id,
            label: `${a.asset_code} — ${a.name}`,
          })),
        },
      },
    },
    {
      // New Functional Location column — sits between Asset and Status so
      // the user can read "where" (FL) right after "what" (asset). Same
      // indigo chip style used on the Instrumentation page for visual
      // continuity across pages that surface FL context.
      id: "functional_location",
      header: "Functional Location",
      cell: ({ row }) => {
        const a = assetById.get(row.original.asset_id);
        const fl = flocForAssetId(row.original.asset_id);
        if (fl) {
          return (
            <span
              className="inline-flex max-w-[12rem] items-center gap-1 truncate rounded-full bg-indigo-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-200 ring-1 ring-indigo-500/30"
              title={`L${fl.level} · ${fl.code} — ${fl.name}`}
            >
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{fl.code} — {fl.name}</span>
            </span>
          );
        }
        // Either the asset hasn't loaded yet (rare race during initial
        // mount) or the asset genuinely has no FL assigned. After the
        // backend embed there's no separate FL-loading state to track.
        if (!a) {
          return (
            <span className="text-[10px] italic text-surface-500">Loading asset…</span>
          );
        }
        return <span className="text-surface-500 text-xs">-</span>;
      },
      filterFn: (row, _id, value) => {
        if (!value) return true;
        const fl = flocForAssetId(row.original.asset_id);
        return fl?.id === value;
      },
      meta: {
        filter: {
          type: "select",
          placeholder: "Any FL",
          options: flocOptions.map((f) => ({
            value: f.id,
            label: `L${f.level} · ${f.code} — ${f.name}`,
          })),
        },
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const overdueDerived = isOverdueTask(row.original);
        const displayStatus =
          overdueDerived && row.original.status === "scheduled"
            ? "overdue"
            : row.original.status;
        return <StatusBadge status={displayStatus} />;
      },
      // Status filter respects the derived "overdue" pseudo-status so
      // selecting Overdue matches scheduled tasks past their due date.
      filterFn: (row, _id, value) => {
        if (!value) return true;
        const t = row.original;
        const effective =
          isOverdueTask(t) && t.status === "scheduled" ? "overdue" : t.status;
        return effective === value;
      },
      meta: {
        filter: {
          type: "select",
          placeholder: "Any status",
          options: STATUS_OPTIONS.map((s) => ({
            value: s,
            label: s.replace(/_/g, " "),
          })),
        },
      },
    },
    {
      accessorKey: "priority",
      header: "Priority",
      cell: ({ row }) =>
        row.original.priority ? (
          <Badge
            variant={
              row.original.priority === "high" || row.original.priority === "critical"
                ? "danger"
                : row.original.priority === "medium"
                  ? "warning"
                  : "default"
            }
          >
            {row.original.priority}
          </Badge>
        ) : (
          <span className="text-surface-500 text-xs">-</span>
        ),
      filterFn: (row, _id, value) =>
        !value || row.original.priority === value,
      meta: {
        filter: {
          type: "select",
          placeholder: "Any priority",
          options: PRIORITY_OPTIONS.map((p) => ({ value: p, label: p })),
        },
      },
    },
    {
      accessorKey: "task_type",
      header: "Type",
      cell: ({ row }) =>
        row.original.task_type ? (
          <Badge variant="info">{row.original.task_type}</Badge>
        ) : (
          <span className="text-surface-500 text-xs">-</span>
        ),
      filterFn: (row, _id, value) =>
        !value || row.original.task_type === value,
      meta: {
        filter: {
          type: "select",
          placeholder: "Any type",
          options: TASK_TYPES.map((t) => ({ value: t, label: t })),
        },
      },
    },
    {
      accessorKey: "scheduled_date",
      header: "Scheduled",
      cell: ({ row }) => {
        if (!row.original.scheduled_date)
          return <span className="text-surface-500 text-xs">-</span>;
        const isOverdue = isOverdueTask(row.original);
        return (
          <div
            className={`flex items-center gap-1 text-xs whitespace-nowrap ${
              isOverdue ? "text-red-400" : "text-surface-400"
            }`}
          >
            <Clock className="h-3 w-3" />
            {formatDate(row.original.scheduled_date)}
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => (
        <span className="text-surface-400 text-xs whitespace-nowrap">
          {formatDate(row.original.created_at)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const actions = availableTransitions(row.original.status);
        return (
          <div className="flex items-center gap-1">
            <button
              onClick={() => openEdit(row.original)}
              title="Edit task"
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-surface-300 bg-surface-700/60 hover:bg-surface-700 ring-1 ring-surface-600/60 transition-colors"
            >
              <Pencil className="h-3 w-3" />
              <span className="hidden lg:inline">Edit</span>
            </button>
            {actions.map((kind) => (
              <ActionButton
                key={kind}
                kind={kind}
                onClick={() => openTransition(row.original, kind)}
              />
            ))}
          </div>
        );
      },
    },
  ];

  const transitionCfg = transitioning
    ? TRANSITION_CONFIG[transitioning.kind]
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Maintenance</h1>
          <p className="text-sm text-surface-400 mt-1">
            Track and manage maintenance tasks
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="icon" onClick={fetchTasks}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" />
            New Task
          </Button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KPICard
          title="Overdue"
          value={overdue}
          icon={<AlertTriangle className="h-5 w-5" />}
          color={overdue > 0 ? "red" : "emerald"}
        />
        <KPICard
          title="Scheduled"
          value={scheduled}
          icon={<Clock className="h-5 w-5" />}
          color="amber"
        />
        <KPICard
          title="In Progress"
          value={inProgress}
          icon={<Timer className="h-5 w-5" />}
          color="blue"
        />
        <KPICard
          title="Completed"
          value={completed}
          icon={<CheckCircle2 className="h-5 w-5" />}
          color="emerald"
        />
      </div>

      <DataTable
        columns={columns}
        data={tasks}
        isLoading={isLoading}
        enableSearch={false}
        emptyMessage="No maintenance tasks found."
        page={page}
        totalPages={totalPages}
        total={total}
        onPageChange={setPage}
      />

      <Modal
        isOpen={showCreate}
        onClose={() => {
          setShowCreate(false);
          setCreateError(null);
        }}
        title="Create Maintenance Task"
      >
        <div className="space-y-4">
          {createError && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {createError}
            </div>
          )}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-surface-300">
              Asset <span className="text-red-400">*</span>
            </label>
            <select
              value={createForm.asset_id}
              onChange={(e) =>
                setCreateForm({ ...createForm, asset_id: e.target.value })
              }
              disabled={isLoadingAssets}
              className="w-full rounded-lg border border-surface-600 bg-surface-800/50 px-4 py-2.5 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 disabled:opacity-50"
            >
              <option value="">
                {isLoadingAssets ? "Loading assets…" : "Select an asset"}
              </option>
              {assets.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.asset_code} — {a.name}
                </option>
              ))}
            </select>
            {!isLoadingAssets && assets.length === 0 && (
              <p className="text-xs text-surface-500">
                No assets found for the selected site.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-surface-300">
              Task Type <span className="text-red-400">*</span>
            </label>
            <select
              value={createForm.task_type}
              onChange={(e) =>
                setCreateForm({ ...createForm, task_type: e.target.value })
              }
              className="w-full rounded-lg border border-surface-600 bg-surface-800/50 px-4 py-2.5 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            >
              {TASK_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-surface-300">
              Description <span className="text-red-400">*</span>
            </label>
            <Input
              placeholder="e.g., Quarterly inspection of pump P-101"
              value={createForm.description}
              onChange={(e) =>
                setCreateForm({ ...createForm, description: e.target.value })
              }
              required
            />
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-surface-300">
              Priority
            </label>
            <select
              value={createForm.priority}
              onChange={(e) =>
                setCreateForm({ ...createForm, priority: e.target.value })
              }
              className="w-full rounded-lg border border-surface-600 bg-surface-800/50 px-4 py-2.5 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-surface-300">
              Scheduled Date <span className="text-red-400">*</span>
            </label>
            <Input
              type="date"
              value={createForm.scheduled_date}
              onChange={(e) =>
                setCreateForm({ ...createForm, scheduled_date: e.target.value })
              }
              required
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowCreate(false);
                setCreateError(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} isLoading={isCreating}>
              Create Task
            </Button>
          </div>
        </div>
      </Modal>

      {/* Status transition modal */}
      <Modal
        isOpen={transitioning !== null}
        onClose={() => {
          if (isApplyingTransition) return;
          setTransitioning(null);
          setTransitionError(null);
        }}
        title={transitionCfg?.title ?? "Update Task"}
      >
        {transitioning && transitionCfg && (
          <div className="space-y-4">
            <div className="rounded-lg border border-surface-700/50 bg-surface-800/40 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-surface-500">
                Task
              </div>
              <div className="mt-0.5 text-sm text-surface-100 line-clamp-2">
                {transitioning.task.description}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-surface-400">
                <StatusBadge status={transitioning.task.status} />
                <span>→</span>
                <StatusBadge status={transitionCfg.targetStatus} />
              </div>
            </div>

            {transitionError && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                {transitionError}
              </div>
            )}

            {transitionCfg.needsCompletedDate && (
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-surface-300">
                  Completion Date <span className="text-red-400">*</span>
                </label>
                <Input
                  type="date"
                  value={transitionForm.completed_date}
                  onChange={(e) =>
                    setTransitionForm({
                      ...transitionForm,
                      completed_date: e.target.value,
                    })
                  }
                  required
                />
              </div>
            )}

            {transitionCfg.needsNotes && (
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-surface-300">
                  {transitionCfg.notesLabel}
                  {transitionCfg.notesRequired && (
                    <span className="text-red-400"> *</span>
                  )}
                </label>
                <textarea
                  value={transitionForm.notes}
                  onChange={(e) =>
                    setTransitionForm({
                      ...transitionForm,
                      notes: e.target.value,
                    })
                  }
                  rows={3}
                  placeholder={
                    transitioning.kind === "complete"
                      ? "What was done, findings, follow-up needed…"
                      : transitioning.kind === "cancel"
                        ? "Why is this task being cancelled?"
                        : transitioning.kind === "reopen"
                          ? "Why is this task being reopened?"
                          : "Optional notes about starting this task…"
                  }
                  className="w-full rounded-lg border border-surface-600 bg-surface-800/50 px-4 py-2.5 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
                {transitioning.task.notes && (
                  <p className="text-[11px] text-surface-500">
                    New notes are appended to existing notes with a timestamp.
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setTransitioning(null);
                  setTransitionError(null);
                }}
                disabled={isApplyingTransition}
              >
                Cancel
              </Button>
              <Button
                onClick={handleTransition}
                isLoading={isApplyingTransition}
              >
                {transitionCfg.confirmLabel}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit task modal — non-status fields only. Status changes go through
          the transition flow above so the audit trail stays consistent. */}
      <Modal
        isOpen={editing !== null}
        onClose={() => {
          if (isSavingEdit) return;
          setEditing(null);
          setEditError(null);
        }}
        title="Edit Maintenance Task"
      >
        {editing && (
          <div className="space-y-4">
            <div className="rounded-lg border border-surface-700/50 bg-surface-800/40 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-surface-500">
                Asset
              </div>
              {(() => {
                const a = assetById.get(editing.asset_id);
                return a ? (
                  <div className="mt-0.5 text-sm text-surface-100">
                    {a.name}{" "}
                    <span className="font-mono text-xs text-surface-400">
                      ({a.asset_code})
                    </span>
                  </div>
                ) : (
                  <div className="mt-0.5 text-sm text-surface-300 font-mono">
                    {editing.asset_id}
                  </div>
                );
              })()}
              <p className="mt-1 text-[11px] text-surface-500">
                The asset can't be reassigned after creation. Delete this task and
                create a new one against a different asset if needed.
              </p>
            </div>

            {editError && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                {editError}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-surface-300">
                Task Type <span className="text-red-400">*</span>
              </label>
              <select
                value={editForm.task_type}
                onChange={(e) =>
                  setEditForm({ ...editForm, task_type: e.target.value })
                }
                className="w-full rounded-lg border border-surface-600 bg-surface-800/50 px-4 py-2.5 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              >
                {TASK_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-surface-300">
                Description <span className="text-red-400">*</span>
              </label>
              <Input
                placeholder="e.g., Quarterly inspection of pump P-101"
                value={editForm.description}
                onChange={(e) =>
                  setEditForm({ ...editForm, description: e.target.value })
                }
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-surface-300">
                  Priority
                </label>
                <select
                  value={editForm.priority}
                  onChange={(e) =>
                    setEditForm({ ...editForm, priority: e.target.value })
                  }
                  className="w-full rounded-lg border border-surface-600 bg-surface-800/50 px-4 py-2.5 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-surface-300">
                  Scheduled Date <span className="text-red-400">*</span>
                </label>
                <Input
                  type="date"
                  value={editForm.scheduled_date}
                  onChange={(e) =>
                    setEditForm({ ...editForm, scheduled_date: e.target.value })
                  }
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-surface-300">
                Notes
              </label>
              <textarea
                value={editForm.notes}
                onChange={(e) =>
                  setEditForm({ ...editForm, notes: e.target.value })
                }
                rows={4}
                placeholder="Free-text notes — past status transitions also append here automatically."
                className="w-full rounded-lg border border-surface-600 bg-surface-800/50 px-4 py-2.5 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setEditing(null);
                  setEditError(null);
                }}
                disabled={isSavingEdit}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} isLoading={isSavingEdit}>
                Save Changes
              </Button>
            </div>

            {/* DMS Documents (Phase 22 — single source of truth) */}
            {selectedSiteId && (() => {
              const a = assetById.get(editing.asset_id);
              const assetLabel = a
                ? `${a.asset_code} - ${a.name}`
                : editing.asset_id;
              // Mirror the backend's _task_folder_label: first 40 chars of
              // description + first 8 chars of UUID. Keeps the upload target
              // aligned with the auto-created folder.
              const cleanDesc = (editing.description || "")
                .replace(/[\\/]/g, " ")
                .split(/\s+/)
                .filter(Boolean)
                .join(" ");
              const stem = cleanDesc.slice(0, 40).replace(/[ \-_]+$/, "");
              const taskLabel = stem
                ? `${stem} - ${editing.id.split("-")[0]}`
                : `Task-${editing.id.split("-")[0]}`;
              return (
                <DMSDocumentsPanel
                  virtualSiteId={selectedSiteId}
                  entityType="maintenance_task"
                  entityId={editing.id}
                  defaultFolderPath={`Maintenance/${assetLabel}/${taskLabel}`}
                  title="Documents"
                />
              );
            })()}
          </div>
        )}
      </Modal>
    </motion.div>
  );
}

function ActionButton({
  kind,
  onClick,
}: {
  kind: TransitionKind;
  onClick: () => void;
}) {
  const config: Record<
    TransitionKind,
    { label: string; icon: typeof Play; className: string }
  > = {
    start: {
      label: "Start",
      icon: Play,
      className:
        "text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 ring-1 ring-blue-500/30",
    },
    complete: {
      label: "Complete",
      icon: CheckCircle,
      className:
        "text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 ring-1 ring-emerald-500/30",
    },
    cancel: {
      label: "Cancel",
      icon: XCircle,
      className:
        "text-red-300 bg-red-500/10 hover:bg-red-500/20 ring-1 ring-red-500/30",
    },
    reopen: {
      label: "Reopen",
      icon: RotateCcw,
      className:
        "text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 ring-1 ring-amber-500/30",
    },
  };
  const c = config[kind];
  const Icon = c.icon;
  return (
    <button
      onClick={onClick}
      title={c.label}
      className={
        "inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors " +
        c.className
      }
    >
      <Icon className="h-3 w-3" />
      <span className="hidden lg:inline">{c.label}</span>
    </button>
  );
}

